import {
  ImageScaleView,
  View,
  ViewOpts,
  animate,
} from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { magicLampsAnim } from './MagicLampsAnim';
import StateObserver from 'src/StateObserver';

class MagicLampsMessageFoundGenie extends View {
  private text1: LangBitmapFontTextView;
  private text2: LangBitmapFontTextView;

  constructor(opts: ViewOpts) {
    super({ ...opts });

    this.text1 = new LangBitmapFontTextView({
      superview: this,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      size: 30,
      font: 'Title',
      width: 500,
      height: 50,
      wordWrap: true,
      localeText: () => i18n('match2.magicLamps.genie01'),
    });

    this.text2 = new LangBitmapFontTextView({
      superview: this,
      y: 30,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      size: 26,
      font: 'Title',
      width: 500,
      height: 50,
      wordWrap: true,
      localeText: () => i18n('match2.magicLamps.genie02'),
    });
  }

  public async animateShow() {
    this.updateOpts({ visible: true, opacity: 0, offsetY: 50 });
    await magicLampsAnim(
      this,
      { opacity: 1, offsetY: 0 },
      500,
      animate.easeOutBack,
    );
  }

  public async animateHide() {
    await magicLampsAnim(this, { opacity: 0, offsetY: 10 }, 200);
    this.updateOpts({ visible: false });
  }
}

class MagicLampsMessagePick extends LangBitmapFontTextView {
  constructor(opts: ViewOpts) {
    super({
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      size: 28,
      font: 'Title',
      width: 500,
      height: 50,
      wordWrap: true,
      localeText: () => i18n('match2.magicLamps.pick'),
      ...opts,
    });
  }

  public async animateShow() {
    this.updateOpts({ visible: true, opacity: 0, offsetY: 20 });
    await magicLampsAnim(this, { opacity: 1, offsetY: 0 }, 300);
  }

  public async animateHide() {
    await magicLampsAnim(this, { opacity: 0, offsetY: -40 }, 200);
    this.updateOpts({ visible: false });
  }
}

class MagicLampsMessageStage extends ImageScaleView {
  constructor(opts: ViewOpts) {
    super({
      align: 'center',
      verticalAlign: 'center',
      image: `assets/ui/match2/magiclamps/magiclamps_stage_02_en.png`,
      width: 181,
      height: 70,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      ...opts,
    });
  }

  public setStage(stage: number) {
    const locale = StateObserver.getLocale();
    this.updateOpts({
      visible: true,
      image: `assets/ui/match2/magiclamps/magiclamps_stage_0${stage}_${locale}.png`,
      opacity: 0,
    });
  }

  public async animateShow() {
    this.updateOpts({
      visible: true,
      opacity: 0,
      scale: 0.5,
    });
    await magicLampsAnim(this, { opacity: 1, scale: 1 }, 300);
  }

  public async animateHide() {
    await magicLampsAnim(this, { opacity: 0, scale: 2 }, 300);
    this.updateOpts({ visible: false });
  }
}

class MagicLampsMessageCongratulations extends View {
  private icon: ImageScaleView;
  private text: LangBitmapFontTextView;

  constructor(opts: ViewOpts) {
    super({ ...opts });

    this.icon = new ImageScaleView({
      superview: this,
      align: 'center',
      verticalAlign: 'center',
      image: `assets/ui/match2/magiclamps/magiclamps_grand_prize_icon.png`,
      width: 150,
      height: 150,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      y: -30,
    });

    this.text = new LangBitmapFontTextView({
      superview: this,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      size: 28,
      font: 'Title',
      width: 500,
      height: 100,
      wordWrap: true,
      y: 70,
      localeText: () => i18n('match2.magicLamps.congratulations'),
    });
  }

  public async animateShow() {
    this.updateOpts({ visible: true, opacity: 0 });
    await magicLampsAnim(this, { opacity: 1 }, 300);
  }

  public async animateHide() {
    await magicLampsAnim(this, { opacity: 0 }, 300);
    this.updateOpts({ visible: false });
  }
}

interface MagicLampsMessages {
  foundGenie: MagicLampsMessageFoundGenie;
  pick: MagicLampsMessagePick;
  stage: MagicLampsMessageStage;
  congratulations: MagicLampsMessageCongratulations;
}

type MagicLampsMessagesKey = keyof MagicLampsMessages;

export class MagicLampsMessage extends View {
  public messages: MagicLampsMessages;
  public current?: MagicLampsMessagesKey;

  constructor(opts: ViewOpts) {
    super({ canHandleEvents: false, ...opts });
    this.messages = {
      foundGenie: new MagicLampsMessageFoundGenie({
        superview: this,
        visible: false,
        x: this.style.width * 0.5,
        y: 285,
      }),
      pick: new MagicLampsMessagePick({
        superview: this,
        visible: false,
        x: this.style.width * 0.5,
        y: 345,
      }),
      stage: new MagicLampsMessageStage({
        superview: this,
        visible: false,
        x: this.style.width * 0.5,
        y: 410,
      }),
      congratulations: new MagicLampsMessageCongratulations({
        superview: this,
        visible: false,
        x: this.style.width * 0.5,
        y: 390,
      }),
    };
  }

  public setStage(stage: number) {
    this.messages.stage.setStage(stage);
  }

  public async showMessage(key: MagicLampsMessagesKey) {
    if (this.current) await this.hideMessage();
    this.current = key;
    await this.messages[key].animateShow();
  }

  public async hideMessage() {
    if (!this.current) return;
    await this.messages[this.current].animateHide();
    this.current = undefined;
  }
}
